import React, { useState, useEffect, useCallback } from 'react';
import { Rnd } from 'react-rnd';
import './CreateCustomTemplatePage.css';
import QRCode from 'react-qr-code';
import { useNavigate } from 'react-router-dom';
import {
  Image as ImageIcon,
  Layers as BgIcon,
  Type as FontSizeIcon,
  Droplet as ColorIcon,
  Type as FontFamilyIcon,
  Trash2 as DeleteIcon,
  AlignLeftIcon,
  AlignCenterIcon,
  AlignRightIcon,
  UndoIcon,
  RedoIcon
} from 'lucide-react';
import { createTemplate } from '../../api/certificateAPI';
import { getMe } from '../../api/loginAPI';
import Swal from 'sweetalert2';
import { createVcTemplate } from '../../api/ssiAPI';
import { publishToHcs } from '../../api/auditAPI';

const defaultStyles = {
  fontSize: 24,
  color: '#000',
  fontFamily: 'Georgia',
  textAlign: 'center',
  fontWeight: 'bold'
};
const DEFAULT_LOGO = '/default_logo.png';
const DEFAULT_BG = '/default_background.png';

export default function CreateCustomTemplatePage({ onSave }) {
  const [formData, setFormData] = useState({
    name: '',               // <- NEW
  type: '', 
    logo: DEFAULT_LOGO,
    background: DEFAULT_BG
  });
const navigate = useNavigate();
const isDarkMode = document.body.classList.contains('dark-mode');


  const [user, setUser] = useState(null);

  useEffect(() => {
    async function fetchUser() {
      const userData = await getMe();
      console.log('UserData', userData)
      setUser(userData);
    }
    fetchUser();
  }, []);

   // Undo/Redo stacks
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [dynamicFields, setDynamicFields] = useState(new Set());

  const [selectedTab, setSelectedTab] = useState(null);
  const templateTypes = ['Internship', 'Training', 'Award', 'Certificate', 'Custom'];
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [logoPosition, setLogoPosition] = useState({ x: 600, y: 20, width: 150, height: 80 });
  const [elements, setElements] = useState([
    {
    id: 'logo',
    type: 'logo',
    value: DEFAULT_LOGO,
    x: 600,
    y: 20,
    width: 150,
    height: 80
  },
    { id: 'certificateName', type: 'text', value: 'Sample Certificate', x: 170, y: 120, width: 500, height: 100, styles: { ...defaultStyles, fontSize: 45 } },
    { id: 'name', type: 'text', value: 'John Doe', x: 220, y: 240, width: 400, height: 60, styles: { ...defaultStyles, fontSize: 30 } },
    { id: 'issuer', type: 'text', value: 'Issued By: EarthID Academy', x: 215, y: 330, width: 400, height: 50, styles: { ...defaultStyles, fontSize: 20 } },
    { id: 'description', type: 'text', value: 'Successfully completed the program.', x: 220, y: 380, width: 400, height: 60, styles: { ...defaultStyles, fontSize: 20 } },
    { id: 'website', type: 'text', value: 'www.myearth.id', x: 5, y: 530, width: 200, height: 30, styles: { ...defaultStyles, fontSize: 15 } },
    { id: 'qrcode', type: 'qrcode', value: 'https://earthid.net/certificate/1234', x: 650, y: 460, width: 80, height: 80 }
  ]);
  const [selectedId, setSelectedId] = useState(null);

// Tab definitions
  const TAB_ITEMS = [
    { key: 'logo', Icon: ImageIcon, label: 'Logo' },
    { key: 'background', Icon: BgIcon, label: 'Background' },
    { key: 'fontSize', Icon: FontSizeIcon, label: 'Size' },
    { key: 'fontColor', Icon: ColorIcon, label: 'Color' },
    { key: 'fontFamily', Icon: FontFamilyIcon, label: 'Font' },
    { key: 'alignment', Icon: AlignCenterIcon, label: 'Align' },
    { key: 'delete', Icon: DeleteIcon, label: 'Delete' }
  ];

  const [bgSize, setBgSize] = useState({ width: 850, height: 550 });
  const [scale, setScale] = useState(1);

  // whenever background changes, load it and grab its dimensions:
 
useEffect(() => {
  const img = new Image();
  img.src = formData.background;
  img.onload = () => setBgSize({ width: 800, height: 565 });;
}, [formData.background]);

 // Snapshot state
  const snapshot = useCallback(() => ({ formData, logoPosition, elements }), [formData, logoPosition, elements]);

  // Push history
  const pushHistory = useCallback(() => {
    setHistory(h => [...h, snapshot()]);
    setFuture([]);
  }, [snapshot]);

  // Undo
  const undo = useCallback(() => {
    setHistory(h => {
      if (!h.length) return h;
      const prev = h[h.length - 1];
      setFuture(f => [snapshot(), ...f]);
      setFormData(prev.formData);
      setLogoPosition(prev.logoPosition);
      setElements(prev.elements);
      return h.slice(0, -1);
    });
  }, [snapshot]);

  // Redo
  const redo = useCallback(() => {
    setFuture(f => {
      if (!f.length) return f;
      const next = f[0];
      setHistory(h => [...h, snapshot()]);
      setFormData(next.formData);
      setLogoPosition(next.logoPosition);
      setElements(next.elements);
      return f.slice(1);
    });
  }, [snapshot]);


 // Keyboard shortcuts
  useEffect(() => {
    const onKey = e => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key.toLowerCase()) {
          case 'z': e.preventDefault(); undo(); break;
          case 'y': e.preventDefault(); redo(); break;
          case 't': e.preventDefault(); pushHistory(); addTextBox(); break;
          case 'l': e.preventDefault(); pushHistory(); handleStyleChange('textAlign','left'); break;
          case 'e': e.preventDefault(); pushHistory(); handleStyleChange('textAlign','center'); break;
          case 'r': e.preventDefault(); pushHistory(); handleStyleChange('textAlign','right'); break;
          case 'd': e.preventDefault(); if (selectedId) { pushHistory(); setElements(el=>el.filter(i=>i.id!==selectedId)); setSelectedId(null);} break;
        }
      }
      if (e.key === 'Delete' && selectedId) {
        pushHistory(); setElements(el=>el.filter(i=>i.id!==selectedId)); setSelectedId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo, pushHistory, selectedId]);

const baseAttributes = [ 'issuerId', 'name', 'certificateName', 'issuer', 'description', 'website' ]

  const handleChange = e => {
    const { name, files } = e.target;
    if (!files?.[0]) return;
    const reader = new FileReader();
    reader.onloadend = () => setFormData(f => ({ ...f, [name]: reader.result }));
    reader.readAsDataURL(files[0]);
  };

  const loadGoogleFont = family => {
    const id = 'gf-' + family;
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/\s+/g, '+')}&display=swap`;
    document.head.append(link);
  };

  const handleStyleChange = (key, val) => {
    if (!selectedId) return;
    if (key === 'fontFamily') loadGoogleFont(val);
    setElements(el =>
      el.map(e =>
        e.id === selectedId
          ? { ...e, styles: { ...e.styles, [key]: val } }
          : e
      )
    );
  };

   // Text change
  const handleTextChange = (id, val) => {
  pushHistory();
  setElements(el =>
    el.map(e => (e.id === id ? { ...e, value: val } : e))
  );

  const newFields = new Set(dynamicFields);
  const matches = val.match(/{(.*?)}/g);
  if (matches) {
    matches.forEach(m => newFields.add(m.replace(/[{}]/g, '').trim()));
    setDynamicFields(newFields);
  }
};


  // Add text
const addTextBox = async () => {
  const { value: variableName } = await Swal.fire({
  title: 'Enter Field Name',
  input: 'text',
  inputPlaceholder: 'e.g. studentName',
  confirmButtonText: 'Add Text Box',
  showCloseButton: true,
  backdrop: true,
  buttonsStyling: false,
  background: document.body.classList.contains('dark-mode') ? '#1f2937' : '#fff',
  color: document.body.classList.contains('dark-mode') ? '#f9fafb' : '#111',
  customClass: {
    popup: 'swal-popup',
    title: 'swal-title',
    confirmButton: 'swal-confirm-btn',
    input: 'swal-input',
    closeButton: 'swal-close-btn'
  }
});

  if (!variableName) return;

  const existingIds = new Set(elements.map(e => e.id));
  if (existingIds.has(variableName)) {
   Swal.fire({
  icon: 'warning',
  title: 'Duplicate Name',
  text: 'This variable name already exists. Please choose another one.',
  background: document.body.classList.contains('dark-mode') ? '#1f2937' : '#fff',
  color: document.body.classList.contains('dark-mode') ? '#f9fafb' : '#111',
  customClass: {
    popup: 'swal-popup',
    confirmButton: 'swal-confirm-btn',
    title: 'swal-title'
  },
  showCloseButton: true,
  buttonsStyling: false
});
    return;
  }

  const newBox = {
    id: variableName,
    type: 'text',
    value: variableName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
    x: 50,
    y: 50 + elements.length * 30,
    width: 200,
    height: 50,
    styles: { ...defaultStyles }
  };

  pushHistory();
  setElements(prev => [...prev, newBox]);
  setSelectedId(variableName);
};

const extraAttributesByType = {
  Internship: ['organization', 'duration', 'mentorName'],
  Training: ['trainer', 'topic', 'duration'],
  Award: ['awardTitle', 'issuedBy', 'date'],
  Certificate: ['issuedDate', 'issuerEmail'],
  Custom: []
};

const saveTemplate = async () => {
  try {
    if (!user || !user.Id) {
      Swal.fire({
        icon: 'warning',
        title: 'Session Expired',
        text: 'Please log in again.'
      });
      return;
    }

    const { value: templateName } = await Swal.fire({
      title: 'Enter Template Name',
      input: 'text',
      inputPlaceholder: 'e.g. Internship Certificate',
      confirmButtonText: 'Add Template'
    });

    if (!templateName) return;

    Swal.fire({ title: 'Saving Template...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
console.log('🔍 Elements:', elements);

    // 🔍 Collect all dynamic variables wrapped in { } from text values
   // 🔁 1. Extract dynamic fields from template
// ✅ 2. Add base attributes to the set
const combinedAttributes = new Set();

// ✅ 1. Add fixed base attributes
baseAttributes.forEach(attr => {
  if (!combinedAttributes.has(attr)) {
    combinedAttributes.add(attr);
  }
});

// ✅ 2. Extract ALL dynamic fields from any text box value
elements.forEach(el => {
  if (el.type === 'text') {
    // Always add its own ID (e.g. 'city')
    combinedAttributes.add(el.id);

    // Also check for dynamic placeholders like {state}
    const matches = el.value.match(/{(.*?)}/g);
    if (matches) {
      matches.forEach(m => combinedAttributes.add(m.replace(/[{}]/g, '').trim()));
    }
  }
});

// 🔁 2. Add template-specific attributes
const selectedType = formData.type || 'Custom';
const typeBasedAttributes = extraAttributesByType[selectedType] || [];
typeBasedAttributes.forEach(attr => combinedAttributes.add(attr));

// 🔁 3. Add manual extra fields (if any)
if (formData.extraFields && Array.isArray(formData.extraFields)) {
  formData.extraFields.forEach(f => combinedAttributes.add(f.trim()));
}

// ✅ Final VC payload
const vcPayload = {
  name: templateName,
  type: selectedType,
  attributes: Array.from(combinedAttributes).map(attr => ({
    key: attr,
    label: attr.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
  }))
};

console.log('📦 Final Combined VC Payload with Type-specific fields:', vcPayload);


 // 🔁 Save VC Template and get the ID
const vcRes = await createVcTemplate(vcPayload);
const vcTemplateId = vcRes?.vcTemplateId;
const vcTemplateName = vcRes?.name

    // ✅ Save visual template
    const payload = {
      userId: user.Id,
      name: templateName,
      vcTemplateName: vcTemplateName,
      background: formData.background,
      logo: formData.logo,
      elements,
    };
console.log('🎨 Visual Template Payload to be saved:', payload);
    const res = await createTemplate(payload);

    const messageObj = {
          "event": "New Template Added",
          "message": `New custom template ${templateName} is created.`,
          "timestamp": new Date().toISOString(),
          "status": "success"
        }
            await publishToHcs(messageObj)
            console.log('Published audit for New Template Added')

    Swal.close();
console.log('✅ VC Template and Visual Template saved successfully');
    if (onSave) onSave(res);
    navigate('/templates');

  } catch (err) {
    console.error('❌ Failed to save template:', err);
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: 'Failed to save template. Please try again.'
    });
  }
};


 // Render tab content
  const renderTab = () => {
    switch(selectedTab) {
      case 'logo': return <div className="upload-field"><label>Logo:</label><input type="file" name="logo" onChange={handleChange}/></div>;
      case 'background': return <div className="upload-field"><label>Background:</label><input type="file" name="background" onChange={handleChange}/></div>;
      case 'fontSize': return <label>Font Size:<input type="number" onChange={e=>handleStyleChange('fontSize',+e.target.value)}/></label>;
      case 'fontColor': {
        const sel = elements.find(e=>e.id===selectedId);
        if(!sel||sel.type!=='text') return <p>Select text.</p>;
        const { color, fontWeight, fontStyle, textDecoration } = sel.styles;
        return (<div className="font-color-and-style">
          <label>Color:<input type="color" value={color} onChange={e=>handleStyleChange('color',e.target.value)}/></label>
          <div className="style-controls">
            <label><input type="checkbox" checked={fontWeight==='bold'} onChange={()=>handleStyleChange('fontWeight',fontWeight==='bold'?'normal':'bold')} />Bold</label>
            <label><input type="checkbox" checked={fontStyle==='italic'} onChange={()=>handleStyleChange('fontStyle',fontStyle==='italic'?'normal':'italic')} />Italic</label>
            <label><input type="checkbox" checked={textDecoration==='underline'} onChange={()=>handleStyleChange('textDecoration',textDecoration==='underline'?'none':'underline')} />Underline</label>
          </div></div>);
      }
      case 'fontFamily': return <label>Font Family:<select onChange={e=>handleStyleChange('fontFamily',e.target.value)}>{['Roboto','Open Sans','Lato','Poppins','Georgia','Arial'].map(f=><option key={f}>{f}</option>)}</select></label>;
      case 'alignment': {
        const sel = elements.find(e=>e.id===selectedId);
        if(!sel||sel.type!=='text') return <p>Select text.</p>;
        return (<div className="alignment-controls">
          <button onClick={()=>handleStyleChange('textAlign','left')}><AlignLeftIcon/></button>
          <button onClick={()=>handleStyleChange('textAlign','center')}><AlignCenterIcon/></button>
          <button onClick={()=>handleStyleChange('textAlign','right')}><AlignRightIcon/></button>
        </div>);
      }
      case 'delete': return <button className="delete-btn" onClick={()=>{if(!selectedId)return; pushHistory(); setElements(el=>el.filter(e=>e.id!==selectedId)); setSelectedId(null);}}><DeleteIcon size={16}/></button>;
      default: return null;
    }
  };


  return (
    <div className="template-pagecc">
      <div className="template-content" style={{ display: 'flex', flexDirection: 'column' }}>
        <h2 className='create-title'>Create Custom Template</h2>
        <div className={`controls-panel ${sidebarOpen ? 'expanded' : 'collapsed'}`} style={{ flex: '0 0 auto' }}>
          <div className="tab-container">
  {/* Horizontal tab bar */}
  <div className="tab-bar">
    {TAB_ITEMS.map(({ key, Icon, label }) => (
      <button
        key={key}
        className={`tab-icon ${selectedTab === key ? 'active' : ''}`}
        onClick={() => {
          setSelectedTab(key);
          setSidebarOpen(true);
        }}
      >
        <Icon />
        <span className="tab-label">{label}</span>
      </button>
    ))}
  </div>

  {/* Tab content below */}
  <div className="tab-content">
    {renderTab()}
  </div>
            <button className="add-text-btn" onClick={addTextBox}>+ Add Text Box</button>
            <div className="undo-redo-controls">
              <button onClick={undo}><UndoIcon /></button>
              <button onClick={redo}><RedoIcon /></button>
            </div>

          </div>
        </div>
        <div className='addTxt'>

        </div>
        
<div className="template-type-selector">
  <label>Select Template Type:</label>
  <select
    value={formData.type || ''}
    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
  >
    <option value="">-- Select Type --</option>
    {templateTypes.map((type) => (
      <option key={type} value={type}>{type}</option>
    ))}
  </select>
</div>




        <div className="preview-panel"
        style={{
     overflow: 'visible',
   }}>
          <h3 className="preview-heading">Live Preview</h3>
          <p style={{ fontSize: '14px', fontStyle: 'italic', marginBottom: '10px', color: '#555' }}>
  Tip: Wrap dynamic data in curly braces like {'{studentName}'} to link it with your VC attributes.
</p>
          {/* scrollable “window” */}
         <div
   className="preview-wrapper"
   style={{
     zoom: scale,               // your computed zoom factor
    display: 'inline-block',   // shrink-wrap to scaled content
    overflow: 'auto',          // scroll if it exceeds viewport
    border: '1px solid #ddd',
    borderRadius: '8px'
   }}
 >
           <div
            className="preview-area"
             style={{
               width:  bgSize.width,
               height: bgSize.height,
               backgroundImage: `url(${formData.background})`
             }}
          >
            {/* <Rnd
  size={logoPosition}
  position={logoPosition}
  bounds="parent"
  onDragStop={(e, d) => setLogoPosition(p => ({ ...p, x: d.x, y: d.y }))}
  onResizeStop={(e, dir, ref, delta, pos) =>
    setLogoPosition({ width: ref.offsetWidth, height: ref.offsetHeight, ...pos })
  }
  onClick={() => setSelectedId('logo')}
  className={`preview-box ${selectedId === 'logo' ? 'selected' : ''}`}
>
  <img src={formData.logo} alt="logo" className="preview-logo-movable" />
</Rnd> */}
            {elements.map(el => (
              <Rnd key={el.id} size={{ width: el.width, height: el.height }} position={{ x: el.x, y: el.y }} bounds="parent"
                onDragStop={(e, d) => setElements(p => p.map(i => i.id === el.id ? { ...i, x: d.x, y: d.y } : i))}
                onResizeStop={(e, dir, ref, delta, pos) => setElements(p => p.map(i => i.id === el.id ? { ...i, width: ref.offsetWidth, height: ref.offsetHeight, ...pos } : i))}
                onClick={() => setSelectedId(el.id)}
                className={`preview-box ${selectedId === el.id ? 'selected' : ''}`}>
                {el.type === 'text' ? (
                  <div className="editable-text" style={el.styles} contentEditable suppressContentEditableWarning onBlur={e => handleTextChange(el.id, e.target.innerText)}>{el.value}</div>
                )  : el.type === 'logo' ? (
      <img src={el.value} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
    ) : (
                  <QRCode value={el.value} style={{ width: '100%', height: '100%' }} />
                )}
              </Rnd>
            ))}
          </div>
          </div>
          <div
  style={{
    position: 'sticky',
    bottom: 0,
    textAlign: 'center',
    zIndex: 10
  }}
>
          <button className="save-btn" onClick={saveTemplate}>Save Template</button> </div>
        </div>
      </div>
    </div>
  );
}